import { Typography } from '@material-ui/core';
import { AttachFile, Cancel } from '@material-ui/icons';
import { errorMessages } from 'common/errorMessages.strings';
import { MdSpace, TextButton } from 'm2m.components';
import React, { Fragment, useRef, useState, useEffect } from 'react';
import * as Papa from 'papaparse';
import { EventoFinanceiroCsv, EventoFinanceiro, TipoEventoFinanceiro } from 'common/genericTypes';
import moment from 'moment';

interface UploadProps {
  disabled?: boolean;
  onChange?(eventosFinanceiros: Array<EventoFinanceiro>): void;
  onError?(error: any): void;
}

export enum AcceptableFileTypes {
  CSV = '.csv',
  TextCSV = 'text/csv',
  ExcelCSV = 'application/vnd.ms-excel',
}

export const defaultAcceptableFileTypes = [
  AcceptableFileTypes.CSV,
  AcceptableFileTypes.TextCSV,
  AcceptableFileTypes.ExcelCSV,
];

const UploadCsv: React.FC<UploadProps> = (props) => {
  const [highlight, setHightlight] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptedFileTypes = defaultAcceptableFileTypes;
  const isValidFileType = (file: File): boolean => acceptedFileTypes.some((type) => file.type === type);

  const handleFileTypeError = () => {
    props.onError?.(errorMessages.validation.upload.invalidExtension);
    setHightlight(false);
  };

  const openFileDialog = () => {
    if (props.disabled) return;
    inputRef?.current?.click();
  };

  const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (props.disabled) return;

    if (event.dataTransfer.files) {
      const newFile: File = event.dataTransfer.files[0];

      if (!isValidFileType(newFile)) {
        handleFileTypeError();
        return;
      }
      setFile(newFile);
    }
    setHightlight(false);
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (props.disabled) return;
    setHightlight(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (props.disabled) return;
    setHightlight(false);
  };

  const onInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (props.disabled) return;
    if (!event.target.files?.[0]) return;

    if (event.target.files) {
      const newFile: File = event.target.files?.[0];
      if (!isValidFileType(newFile)) {
        handleFileTypeError();
        return;
      }
      setFile(newFile);
    }
    setHightlight(false);
  };

  const onDeleteClick = () => {
    setFile(null);
    if (inputRef?.current) inputRef.current.value = '';
    if (props.onChange) props.onChange([]);
  };

  const handleUploadError = (message: string) => {
    props.onError?.(message);
    setFile(null);
    throw message;
  };


  const parseCsv = (result: Papa.ParseResult<EventoFinanceiroCsv>) => {
    const eventosFinanceiros: Array<EventoFinanceiro> = result.data.filter((row) => { return row.data && row.tipo }).map((row) => {
      if (!Object.values(TipoEventoFinanceiro).some((tipo) => row.tipo === tipo)) {
        handleUploadError(errorMessages.instrumentoFinanceiro.eventosFinanceiros.eventoInvalido);
      }
      const data = moment(row.data, 'DD/MM/YYYY');
      if (!data.isValid()) {
        handleUploadError(errorMessages.instrumentoFinanceiro.eventosFinanceiros.dataInvalida);
      }
      if (row.tipo === TipoEventoFinanceiro.Amortizacao && !row.percentual) {
        handleUploadError(errorMessages.instrumentoFinanceiro.eventosFinanceiros.percentualAmortizacao);
      }
      let percentualValue = Number(row.percentual?.trim().replaceAll(",", "."));
      const percentual = row.tipo !== TipoEventoFinanceiro.Amortizacao ? null : percentualValue;
      return { tipo: row.tipo as TipoEventoFinanceiro, data, percentual };
    });
    if (props.onChange) props.onChange(eventosFinanceiros);
  };

  useEffect(() => {
    if (file) {
      Papa.parse<EventoFinanceiroCsv>(file, {
        header: true,
        skipEmptyLines: true,
        complete: parseCsv,
      });
    }
  }, [file]);

  return (
    <Fragment>
      <div
        onClick={openFileDialog}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          width: '100%',
          border: '1px dashed #D7D8D9',
          borderRadius: '8px',
          cursor: props.disabled ? 'default' : 'pointer',
          textAlign: 'center',
        }}
      >
        <MdSpace />
        <AttachFile fontSize="small" />
        <Typography color="textSecondary" variant="body2">
          {highlight ? 'Solte para carregar o arquivo' : 'Arraste e solte ou SELECIONE O DOCUMENTO .csv'}
        </Typography>
        <input
          ref={inputRef}
          type="file"
          accept={acceptedFileTypes.join(',')}
          style={{ display: 'none' }}
          onChange={onInputChange}
        />
        <MdSpace />
      </div>
      {file && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2">{file.name}</Typography>
          <TextButton onClick={onDeleteClick} startIcon={<Cancel />}>
            {'Remover'}
          </TextButton>
        </div>
      )}
    </Fragment>
  );
};

export default UploadCsv;
